// This should be the ONLY file with moment and rrule imported
// They are HEAVY dependencies that we will want to replace in the future

/* eslint-disable-next-line no-restricted-imports */
import moment from "moment-timezone";
/* eslint-disable-next-line no-restricted-imports */

export enum DATE_FORMAT {
  dateLong = "ddd, MMMM D",
  dateShort = "ddd M/D",
  monthAbbreviation = "MMM",
  day = "D",
  dayAndDate = "D ddd",
  dayAndMonthAndDay = "ddd MMM D",
  monthAndYear = "MMMM YYYY",
  monthDayAndYear = "MMM D, Y",
  monthFullDayAndYear = "MMMM D, Y",
  dateInputMask = "MM/DD/YY",
  rrule = "MMM D",
  time = "h:mm A",
  timeWithTimezone = "timeWithTimezone", // We have custom abbreviations, so can't use z,
  edenDate = "edenDate",
}

export const multiDayEventEndDate = (date: string, timezone: string) => {
  // FROM EDEN
  // All day events end at midnight the following day, so to get the accurate end day
  // we need to subtract time (arbitrarily an hour, consistent with the client handling)
  return moment(date).tz(timezone).subtract(1, "hour");
};

export const isMultiDay = (
  start: string,
  end: string,
  timezone: string
): boolean =>
  moment(start).tz(timezone).isSame(multiDayEventEndDate(end, timezone), "day")
    ? false
    : true;

const tzAbbreviationReplacements: {
  [key: string]: string;
} = {
  EST: "ET",
  EDT: "ET",
  CST: "CT",
  CDT: "CT",
  MST: "MT",
  MDT: "MT",
  PST: "PT",
  PDT: "PT",
};

export const getTimezoneAbbreviation = (timezone: string) => {
  const momentAbbreviation = moment.tz(timezone).zoneAbbr();

  return tzAbbreviationReplacements[momentAbbreviation] || momentAbbreviation;
};

export const getBrowserTimezone = (fallbackTimezone = "America/New_York") => {
  try {
    // Doesn't work in all browsers
    return (
      Intl.DateTimeFormat().resolvedOptions().timeZone ||
      moment.tz.guess() ||
      fallbackTimezone
    );
  } catch (e) {
    return fallbackTimezone;
  }
};

export const formatDate = (
  date: moment.Moment | Date | string,
  timezone: string = getBrowserTimezone(),
  format: DATE_FORMAT
) => {
  if (format === DATE_FORMAT.timeWithTimezone) {
    // We have custom abbreviations, so can't use z
    return `${moment(date)
      .tz(timezone)
      .format(DATE_FORMAT.time)} ${getTimezoneAbbreviation(timezone)}`;
  }

  if (format === DATE_FORMAT.edenDate) {
    return moment(date).format();
  }

  return moment.tz(date, timezone).format(format);
};

export const dateFromFormatted = (formattedDate: string, format: DATE_FORMAT) =>
  moment(formattedDate, format).toDate();

export const validateFormat = (
  date: moment.Moment | Date | string,
  format: DATE_FORMAT
) => {
  return moment(date, format, true).isValid();
};

export const dateComparator = (
  dateA: moment.Moment | Date | string,
  dateB: moment.Moment | Date | string
) => (moment(dateA).isSame(dateB) ? 0 : moment(dateA).isBefore(dateB) ? -1 : 1);

export const add = (
  date: moment.Moment | Date | string,
  {
    days,
    hours,
    minutes,
    months,
  }: { days?: number; hours?: number; minutes?: number; months?: number }
) =>
  moment(date)
    .add(days || 0, "days")
    .add(hours || 0, "hours")
    .add(minutes || 0, "minutes")
    .add(months || 0, "months");

export const subtract = (
  date: moment.Moment | Date | string,
  { days, hours, minutes }: { days?: number; hours?: number; minutes?: number }
) =>
  moment(date)
    .subtract(days || 0, "days")
    .subtract(hours || 0, "hours")
    .subtract(minutes || 0, "minutes");

// Human-friendly relative time, e.g. "5 minutes ago" — used for the team "last updated" label.
export const fromNow = (date: moment.Moment | Date | string) => moment(date).fromNow();

export const getNow = () => moment().format();
export const getIsAfternoon = () => moment().hours() > 12;
export const getStartOfHour = () => moment().startOf("hour").format();
export const getLocalDate = (date: string) =>
  moment(date).startOf("day").toDate();
/**
 * converts an ISOTimestamp into a date
 * @param timestamp a timestamp using ISO8601 format (e.g. 2013-03-07T07:00:00+08:00)
 * @returns a date object
 */
export const parseISOTimestamp = (timestamp: string) =>
  moment(timestamp).toDate();

export const mergeDateTime = (date: Date, time: string): Date => {
  const timeDate = dateFromFormatted(time, DATE_FORMAT.time);
  return moment(date)
    .startOf("day")
    .set({ hours: timeDate.getHours(), minutes: timeDate.getMinutes() })
    .toDate();
};

export const getDaysInRange = (start: string, end: string) => {
  const range: string[] = [];
  let current = formatDate(start, undefined, DATE_FORMAT.edenDate);

  while (dateComparator(current, end) < 0) {
    range.push(current);
    current = formatDate(
      add(current, { days: 1 }),
      undefined,
      DATE_FORMAT.edenDate
    );
  }

  return range;
};

export type Duration = moment.Duration;
export const calcDuration = (start: string, end: string): Duration =>
  moment.duration(moment(end).diff(moment(start)));

export const durationInMinutes = (start: string, end: string) =>
  moment(start).diff(moment(end), "minutes");

export const getWeekdayName = (dt: string): string => moment(dt).format("dddd");

export const getRRuleParam = (
  rule: string | undefined,
  matchString: string
): string => {
  const splits = (rule || "").split(";");
  const matchingItem = splits.find((x) => x.startsWith(matchString));
  const match = matchingItem?.split("=")[1].replace(";", "");
  return match ? match : "";
};
